import type { ActionGraph, ActionType } from "../actions.ts";
import {
	LEGACY_EVENT_TYPES,
	getActionSpec,
	getRequiredEntryPoints,
	isDataProducer,
} from "../schemaSpec.ts";
import type { Item, ProjectView } from "../entities.ts";
import { EVENT_TYPE_SET, issue } from "./helpers.ts";
import type { Issue, ValidationReport } from "./types.ts";

type OrphanNodeOptions = {
	entityKind?: Issue["entityKind"];
	allowReactiveUiConsumers?: boolean;
};

const REACTIVE_UI_CONSUMERS = new Set<string>([
	"SetUiProperty",
	"TweenProperty",
]);

const hasInputBindings = (node: { inputs?: unknown }): boolean =>
	typeof node.inputs === "object" &&
	node.inputs !== null &&
	!Array.isArray(node.inputs) &&
	Object.keys(node.inputs).length > 0;

const getInputSourceNodeIds = (node: { inputs?: unknown }): string[] => {
	if (
		typeof node.inputs !== "object" ||
		node.inputs === null ||
		Array.isArray(node.inputs)
	) {
		return [];
	}

	const sourceIds: string[] = [];
	for (const binding of Object.values(node.inputs)) {
		if (
			typeof binding !== "object" ||
			binding === null ||
			Array.isArray(binding)
		) {
			continue;
		}
		const sourceNodeId = (binding as { sourceNodeId?: unknown }).sourceNodeId;
		if (typeof sourceNodeId === "string" && sourceNodeId !== "") {
			sourceIds.push(sourceNodeId);
		}
	}
	return sourceIds;
};

const hasDataOutputs = (nodeType: string): boolean => {
	const actionSpec = getActionSpec(nodeType as ActionType);
	return (actionSpec?.dataOutputs?.length ?? 0) > 0;
};

const isReactiveUiConsumer = (
	node: { type: string; inputs?: unknown },
	options: OrphanNodeOptions,
): boolean =>
	options.allowReactiveUiConsumers === true &&
	REACTIVE_UI_CONSUMERS.has(node.type) &&
	hasInputBindings(node);

const collectLiveDataSources = (
	graph: ActionGraph,
	flowTargets: Set<string>,
	options: OrphanNodeOptions,
): Set<string> => {
	const live = new Set<string>();
	const stack: string[] = [];

	for (const [nodeId, node] of Object.entries(graph.nodes)) {
		if (flowTargets.has(nodeId) || isReactiveUiConsumer(node, options)) {
			stack.push(...getInputSourceNodeIds(node));
		}
	}

	while (stack.length > 0) {
		const nodeId = stack.pop();
		if (!nodeId || live.has(nodeId)) continue;

		const node = graph.nodes[nodeId];
		if (!node || !hasDataOutputs(node.type)) continue;

		live.add(nodeId);
		stack.push(...getInputSourceNodeIds(node));
	}

	return live;
};

export const validateEntryPoints = (
	graph: ActionGraph,
	item: Item,
	view: ProjectView,
	entityId: string,
	report: ValidationReport,
): void => {
	const itemStatKeys = Object.keys(item.stats ?? {});
	const expected = getRequiredEntryPoints(
		item.category ?? null,
		itemStatKeys,
		view.stats,
	);
	const presentEventTypes = new Set<string>();
	const legacyNodes: { id: string; type: string }[] = [];
	for (const node of Object.values(graph.nodes)) {
		if (EVENT_TYPE_SET.has(node.type)) {
			presentEventTypes.add(node.type);
		} else if (LEGACY_EVENT_TYPES.includes(node.type)) {
			legacyNodes.push({ id: node.id, type: node.type });
		}
	}
	for (const eventType of expected) {
		if (!presentEventTypes.has(eventType)) {
			report.errors.push(
				issue(
					"error",
					"items",
					entityId,
					["graph", "nodes"],
					`Missing required entry point '${eventType}' for category '${item.category ?? "default"}'`,
					"missing_entrypoint",
					{ actionType: eventType },
				),
			);
		}
	}
	for (const legacy of legacyNodes) {
		report.warnings.push(
			issue(
				"warning",
				"items",
				entityId,
				["graph", "nodes", legacy.id],
				`Legacy entry point '${legacy.type}' is no longer supported — remove the node or replace with OnSpawn + Interact`,
				"legacy_entrypoint",
				{ nodeId: legacy.id, actionType: legacy.type },
			),
		);
	}
};

export const validateOrphanNodes = (
	graph: ActionGraph,
	entityId: string,
	report: ValidationReport,
	options: OrphanNodeOptions = {},
): void => {
	const entityKind = options.entityKind ?? "items";
	const targeted = new Set<string>();
	for (const edge of graph.edges) targeted.add(edge.target);
	const liveDataSources = collectLiveDataSources(graph, targeted, options);
	for (const [nodeId, node] of Object.entries(graph.nodes)) {
		if (EVENT_TYPE_SET.has(node.type)) continue;
		const actionSpec = getActionSpec(node.type as ActionType);
		if (actionSpec && isDataProducer(actionSpec)) continue;
		if (liveDataSources.has(nodeId)) continue;
		if (isReactiveUiConsumer(node, options)) continue;
		if (targeted.has(nodeId)) continue;
		report.warnings.push(
			issue(
				"warning",
				entityKind,
				entityId,
				["graph", "nodes", nodeId],
				`Node '${node.type}' is unreachable — no edge targets it. Connect it from an EntryPoint or another action.`,
				"orphan_node",
				{ nodeId, actionType: node.type },
			),
		);
	}
};
